import React, {useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import {createFilterOptions, Divider, Paper} from "@mui/material";
import Chips, {ChipsTypes} from "../../../../../components/base/chips/chips";
import {OutlinedAutoComplete} from "../../../../../components/base/auto_complete/outlined_auto_complete";
import Api from "../../../../../../core/services/api/api";
import {OperatorDashboardsDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import SearchInput from "../../../../../components/base/input/search_input";
import {useHistory, useParams} from "react-router-dom";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";

const dataGridSaveConfig = {
    name: 'OperatorDashboardsViewDataGrid',
    version: 2,
}

const OperatorDashboards = () => {
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [keywords, setKeywords] = useState('');
    const [dashboards, setDashboards] = useState([]);
    const isMounted = useIsMounted();
    const params = useParams();
    const history = useHistory();
    const selectedGroup = useMemo(() => groups?.find(e => e.id === Number(params.groupId)), [groups, params.groupId]);
    const {configureNotebookTags} = useConfigureNotebook('operator-dashboards-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.operatorsDashboards(params.groupId));
        configureBehavioralTool((tags) => tags.operatorsDashboards(params.groupId));
    }, [configureBehavioralTool, configureNotebookTags, params.groupId]);

    /**
     * If the group id is not in the list of groups, redirects to the base url of the operator dashboards.
     */
    useEffect(() => {
        if (!groups.length || !params.groupId)
            return;
        const group = groups.find(e => e.id === Number(params.groupId));
        if (!group) {
            history.replace(routes.operators.dashboards.base);
        }
    }, [params.groupId, groups, history]);
    
    const visibleGroups = useMemo(() => {
        let res = Array.from(groups);
        return res.filter(e => (!keywords || (e.name?.toLowerCase()?.includes(keywords?.toLowerCase()))) && (!params.groupId || e.id === Number(params.groupId)));
    }, [groups, keywords, params.groupId])

    const columns = useMemo(() =>
            /**@type {DataGridColumn[]}*/
            [
                {
                    title: "Group Title",
                    name: OperatorDashboardsDataGridCellKeys.title,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: 200,
                },
                {
                    title: "Dashboards",
                    name: OperatorDashboardsDataGridCellKeys.dashboards,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    width: {
                        minWidth: 500,
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                    sortable: false,
                },
            ],
        [])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * As soon as the component mounts:
     * - fetches all the operator groups and available dashboards from the server.
     */
    useEffect(() => {
        getAllGroupsAndDashboards().then()
    }, [])

    /**
     * Fetches all the operator groups and available dashboards from the server.
     */
    const getAllGroupsAndDashboards = async () => {
        setLoading(true);
        const response = await Api.getAllOperatorGroupsWithAssignedDashboards();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setGroups(response.data.allGroups?.filter(e => e.token !== '00000000-0000-0000-0000-000000000000'));
            setDashboards(response.data.allAvailableDashboards);
        }
    }


    /**
     * Adds a dashboard to a selected record by calling the api and if the result is successful, appends the
     * dashboard to record's dashboards list.
     */
    const addDashboardToGroup = (group, dashboard) => {
        Api.addDashboardToOperatorGroup(dashboard.id, group.id).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setGroups(prevState => prevState.map(e => e.id === group.id ? {
                    ...e,
                    dashboards: e.dashboards ? [...e.dashboards, dashboard] : [dashboard],
                } : e));
            }
        });
    }

    /**
     * Removes a dashboard from a selected record by calling the api and if the result is successful, removes the
     * dashboard from record's dashboards list.
     */
    const removeDashboardFromGroup = (group, dashboard) => {
        Api.removeDashboardFromOperatorGroup(dashboard.id, group.id).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setGroups(prevState => prevState.map(e => e.id === group.id ? {
                    ...e,
                    dashboards: e.dashboards?.filter(e => e.id !== dashboard.id) ?? [],
                } : e));
            }
        });
    }

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return visibleGroups.map(group => {
            return {
                key: group.id,
                data: group,
                cells: {
                    [OperatorDashboardsDataGridCellKeys.title]: group.name,
                    [OperatorDashboardsDataGridCellKeys.dashboards]:
                        <GroupDashboards
                            onAdd={(dashboard) => addDashboardToGroup(group, dashboard)}
                            onRemove={(dashboard) => removeDashboardFromGroup(group, dashboard)}
                            group={group}
                            dashboards={dashboards}
                            loading={loading}
                        />,
                },
            }
        })
    }, [dashboards, loading, visibleGroups])

    return (
        <>
            <Row className='no-gutters w-100 justify-content-start align-items-center my-4'>
                <p className='text-lg font-weight-semi-bold text-color-1'>
                    {selectedGroup ? `${selectedGroup.name ?? ""}'s Dashboards` : "Operator Dashboards"}
                </p>
            </Row>
            <Paper className={`paper w-100 mb-3`}>
                <Row className='no-gutters align-items-center justify-content-between w-100'>
                    <Col xs={12} className='px-3'>
                        <SearchInput
                            autoSearch
                            search={setKeywords}
                            initialValue={keywords}
                            label={'Search'}
                            placeholder={'Group title'}
                        />
                    </Col>
                    <Divider className='divider'/>
                    <div className='w-100 my-2'/>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        state={{
                            loading: {state: loading}
                        }}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        hideFooter
                        hideToolbar
                        visibleRows={dataGridRows.length}
                    />
                </Row>
            </Paper>
        </>
    );
}

const filter = createFilterOptions();

const GroupDashboards = ({group, onAdd, onRemove, dashboards, loading}) => {
    const [autoCompleteText, setAutoCompleteText] = useState('');
    const [autoCompleteValue, setAutoCompleteValue] = useState(null);

    /**
     * Depending on the type of the selected dashboard:
     * if string => sets it as the text of the auto complete
     * if object => calls the onAdd callback with the dashboard as its argument.
     * @param event
     * @param dashboard
     */
    const onSelectNewDashboard = (event, dashboard) => {
        if (!dashboard) dashboard = ''
        if (typeof dashboard === 'string') setAutoCompleteText(dashboard)
        else if (typeof dashboard === 'object') {
            onAdd(dashboard.value);
            setAutoCompleteValue(null);
            setAutoCompleteText('');
        }
    }

    /**
     * Creates the options fo the auto compalte
     * @param option
     * @return {JSX.Element|string}
     */
    const renderAutoCompleteDashboard = (props, option) => {
        if (!option) return '';
        if (typeof option === 'string') return (
            <p>{option}</p>
        );
        return <p>{option?.title}</p>
    }

    /**
     * Filters the dashboards of the auto complete by stripping those that are already assigned to the record
     * @param o {any}
     * @param p {any}
     * @return {any[]}
     */
    const filterOptions = (o, p) => {
        const options = filter(o, p);
        const res = [];
        const assignedDashboards = group.dashboards?.map(e => e.id) ?? [];
        options.forEach(option => {
            if (!assignedDashboards.includes(option.value.id)) {
                res.push(option);
            }
        })
        return res;
    }

    const chips = useMemo(() => {
        return group?.dashboards?.map(dashboard =>
            <div {...dataGridCellTextProps} key={dashboard.id} className='mb-2 mr-2'>
                <Chips
                    type={ChipsTypes.remove}
                    content={dashboard.title}
                    onIconClick={() => onRemove(dashboard)}
                />
            </div>
        )
    }, [group?.dashboards])

    return (
        <Row className='no-gutters w-100 px-1 align-items-center justify-content-start flex-wrap'>
            {chips}
            <div className={'mb-2 mr-2'}>
                <OutlinedAutoComplete
                    style={{
                        minWidth: 200
                    }}
                    value={autoCompleteValue}
                    loading={loading}
                    onSelect={onSelectNewDashboard}
                    onChange={(e) => setAutoCompleteText(e.target.value)}
                    clearOnBlur
                    freeSolo={false}

                    type={'text'}
                    variant={'outlined'}
                    inputValue={autoCompleteText}
                    filterOptions={filterOptions}
                    getOptionSelected={(o) => false}
                    options={dashboards?.map(dashboard => ({
                        title: `${dashboard.title}`,
                        value: dashboard,
                    })) ?? []}
                    renderOption={renderAutoCompleteDashboard}
                    loadingText={
                        <div className='loading auto-complete'>
                            <div/>
                            <div/>
                            <div/>
                            <div/>
                        </div>
                    }
                />
            </div>
        </Row>
    );
}


export default OperatorDashboards;
